/**
 * The Living Legacy of Liamuiga — collection registry.
 *
 * A single source of truth for the seven-chapter editorial collection so new
 * chapters can be added without touching layout components. Client-side only.
 */
import mountLiamuiga from "@/assets/blog/mount-liamuiga-hike.jpg.asset.json";

export type ChapterStatus = "published" | "coming-soon";
export type Soundscape = "rainforest" | "rivers" | "coastline" | "mountain-winds";

export interface ChapterSourceGroup {
  heading: string;
  entries: string[];
}

export interface Chapter {
  number: number;
  slug: string;
  route: string;
  title: string;
  subtitle: string;
  status: ChapterStatus;
  readingMinutes: number;
  soundscape: Soundscape;
  heroImage?: string;
  /** Slugs of botanicals featured in this chapter (from botanicalPassport.ts) */
  botanicals?: string[];
  sources?: ChapterSourceGroup[];
  description?: string;
}

const CHAPTER_ONE_SOURCES: ChapterSourceGroup[] = [
  {
    heading: "Archaeological Sources",
    entries: [
      "Rouse, I. — The Tainos: Rise and Decline of the People Who Greeted Columbus (Yale, 1992)",
      "Reid, B. — Myths and Realities of Caribbean History (University of Alabama Press, 2009)",
      "St. Kitts Heritage Society — archaeological surveys of pre-Columbian settlement sites",
    ],
  },
  {
    heading: "Historical Records",
    entries: [
      "Higham, J.J.S. — The Development of the Leeward Islands under the Restoration",
      "Colonial Office records held at the National Archives, Kew",
      "St. Christopher Heritage Society — parish and estate records",
    ],
  },
  {
    heading: "Oral Traditions",
    entries: [
      "Community elders of Sandy Point, Molineux, and Old Road villages — recorded oral histories",
      "Kittitian and Nevisian storytelling traditions preserved through generations",
    ],
  },
  {
    heading: "Indigenous Perspectives",
    entries: [
      "Kalinago Council of Dominica — contemporary Indigenous scholarship",
      "Forte, M. — Ruins of Absence, Presence of Caribs: (Post)Colonial Representations of Aboriginality in Trinidad and Tobago",
      "Where interpretations differ between sources, we acknowledge them respectfully rather than choose one over another.",
    ],
  },
  {
    heading: "Botanical References",
    entries: [
      "Honychurch, P.N. — Caribbean Wild Plants and Their Uses",
      "St. Kitts Department of Agriculture — plant inventories",
      "Sugar Town Organics field notes and community knowledge holders",
    ],
  },
  {
    heading: "Photography & Artwork Credits",
    entries: [
      "Sugar Town Organics editorial photography, St. Kitts",
      "Botanical illustration and archival imagery used under fair use for educational purposes",
    ],
  },
];

export const CHAPTERS: Chapter[] = [
  {
    number: 1,
    slug: "chapter-one",
    route: "/liamuiga",
    title: "Liamuiga: The Fertile Island",
    subtitle: "Before the island was called St. Kitts",
    status: "published",
    readingMinutes: 18,
    soundscape: "rainforest",
    heroImage: mountLiamuiga.url,
    botanicals: [
      "sea-grape",
      "genip",
      "guava",
      "wild-ginger",
      "cassava",
      "sweet-potato",
      "cotton",
      "calabash",
      "mahoe",
    ],
    sources: CHAPTER_ONE_SOURCES,
    description:
      "Long before European sails appeared on the horizon, this island was Liamuiga — the fertile island. The opening chapter of The Living Legacy of Liamuiga.",
  },
  {
    number: 2,
    slug: "chapter-two",
    route: "/living-legacy-of-liamuiga/chapter-two",
    title: "The People of the Islands",
    subtitle: "Kalinago, Taíno, and the first families of Liamuiga",
    status: "coming-soon",
    readingMinutes: 20,
    soundscape: "rainforest",
  },
  {
    number: 3,
    slug: "chapter-three",
    route: "/living-legacy-of-liamuiga/chapter-three",
    title: "The Sea Roads",
    subtitle: "Canoes, currents, and a connected Caribbean",
    status: "coming-soon",
    readingMinutes: 16,
    soundscape: "coastline",
  },
  {
    number: 4,
    slug: "chapter-four",
    route: "/living-legacy-of-liamuiga/chapter-four",
    title: "Plants, Food & Healing",
    subtitle: "The living pantry and medicine cabinet of the island",
    status: "coming-soon",
    readingMinutes: 22,
    soundscape: "rainforest",
  },
  {
    number: 5,
    slug: "chapter-five",
    route: "/living-legacy-of-liamuiga/chapter-five",
    title: "Sacred Marks",
    subtitle: "Petroglyphs, ceremony, and the shape of belief",
    status: "coming-soon",
    readingMinutes: 18,
    soundscape: "mountain-winds",
  },
  {
    number: 6,
    slug: "chapter-six",
    route: "/living-legacy-of-liamuiga/chapter-six",
    title: "A Story Remembered",
    subtitle: "How the memory of Liamuiga was kept, and by whom",
    status: "coming-soon",
    readingMinutes: 20,
    soundscape: "rivers",
  },
  {
    number: 7,
    slug: "chapter-seven",
    route: "/living-legacy-of-liamuiga/chapter-seven",
    title: "Legacy",
    subtitle: "What continues to grow from Liamuiga today",
    status: "coming-soon",
    readingMinutes: 15,
    soundscape: "rainforest",
  },
];

export const COLLECTION_TITLE = "The Living Legacy of Liamuiga";
export const COLLECTION_TAGLINE =
  "A cinematic journey into the Indigenous history, botanical heritage, and living traditions of St. Kitts before European colonisation.";
export const COLLECTION_ROUTE = "/liamuiga";

export function getChapter(slug: string): Chapter | undefined {
  return CHAPTERS.find((c) => c.slug === slug);
}

export function getChapterByRoute(pathname: string): Chapter | undefined {
  return CHAPTERS.find((c) => c.route === pathname);
}

export function getPublishedChapters(): Chapter[] {
  return CHAPTERS.filter((c) => c.status === "published");
}

export function getNextChapter(slug: string): Chapter | undefined {
  const idx = CHAPTERS.findIndex((c) => c.slug === slug);
  if (idx < 0) return undefined;
  return CHAPTERS[idx + 1];
}

/* ------------------------------------------------------------------ *
 * Reading progress — persistent per-visitor across chapters
 * ------------------------------------------------------------------ */

const PROGRESS_KEY = "liamuiga:progress:v1";

export interface ProgressState {
  completed: string[]; // chapter slugs
  lastRead?: string;
}

export function loadProgress(): ProgressState {
  if (typeof window === "undefined") return { completed: [] };
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    if (!raw) return { completed: [] };
    const parsed = JSON.parse(raw) as ProgressState;
    return { completed: parsed.completed ?? [], lastRead: parsed.lastRead };
  } catch {
    return { completed: [] };
  }
}

export function markChapterRead(slug: string) {
  if (typeof window === "undefined") return;
  const state = loadProgress();
  if (!state.completed.includes(slug)) state.completed.push(slug);
  state.lastRead = slug;
  window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent("liamuiga:progress"));
}
